import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { extractApiError } from '../../utils/pdfDownload';

interface PlaceRow {
  id: string;
  label: string;
  street: string;
  street_number: string;
  postal_code: string;
  city: string;
  latitude: number;
  longitude: number;
}

const emptyForm = {
  label: '',
  street: '',
  street_number: '',
  postal_code: '',
  city: '',
};

export const SavedPlacesSection: React.FC = () => {
  const [places, setPlaces] = useState<PlaceRow[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/users/me/places');
      setPlaces(res.data || []);
    } catch {
      setPlaces([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const add = async () => {
    if (!form.label.trim() || !form.street.trim() || !form.city.trim()) {
      toast.error('Label, street and city are required');
      return;
    }
    setSaving(true);
    try {
      const address = `${form.street} ${form.street_number}, ${form.postal_code} ${form.city}`;
      const geo = await api.get('/geocode', { params: { address } });
      await api.post('/users/me/places', {
        ...form,
          latitude: Number(geo.data.latitude),
          longitude: Number(geo.data.longitude),
          street_number: form.street_number.trim() || '-',
      });
      setForm(emptyForm);
      toast.success('Place saved');
      await load();
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/users/me/places/${id}`);
      await load();
    } catch (err) {
      toast.error(extractApiError(err).message);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Saved places</h2>
      <p className="text-sm text-gray-500 mb-3">
        Warehouse, home, and other start/end points for delivery tours.
      </p>
      <ul className="space-y-2 mb-4">
        {places.map((place) => (
          <li
            key={place.id}
            className="flex items-center justify-between bg-white border rounded-xl px-3 py-2"
          >
            <div>
              <p className="font-bold text-gray-900">{place.label}</p>
              <p className="text-xs text-gray-500">
                {place.street} {place.street_number}, {place.postal_code}{' '}
                {place.city}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void remove(place.id)}
              className="text-xs font-bold text-red-600"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 gap-2">
        <input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Label (Warehouse)"
          className="col-span-2 px-3 py-2 border rounded-lg text-sm"
        />
        <input
          value={form.street}
          onChange={(e) => setForm({ ...form, street: e.target.value })}
          placeholder="Street"
          className="px-3 py-2 border rounded-lg text-sm"
        />
        <input
          value={form.street_number}
          onChange={(e) => setForm({ ...form, street_number: e.target.value })}
          placeholder="No."
          className="px-3 py-2 border rounded-lg text-sm"
        />
        <input
          value={form.postal_code}
          onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
          placeholder="PLZ"
          className="px-3 py-2 border rounded-lg text-sm"
        />
        <input
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="City"
          className="px-3 py-2 border rounded-lg text-sm"
        />
      </div>
      <button
        type="button"
        disabled={saving}
        onClick={() => void add()}
        className="mt-3 px-4 py-2 bg-brand-600 text-white rounded-lg font-medium disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Add place'}
      </button>
    </div>
  );
};
